import React from 'react';
import {
  Paper,
  TextInput,
  Select,
  Button,
  Group,
  Stack,
  Grid,
  Collapse,
  ActionIcon,
  Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconSearch, IconFilter, IconX, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { StoricoFilter } from '../../types/storico-types';

interface StoricoFiltersProps {
  filters: StoricoFilter;
  onFiltersChange: (filters: StoricoFilter) => void;
  onSearch: () => void;
  onReset: () => void;
  categorie: string[];
  statiProgetto: string[];
  /** Tipologie raw dal DB (es. "Bozzetto", "Campione", "-" per Produzione). */
  tipologie?: string[];
  /** Anni distinti nello storico. */
  anni?: number[];
  loading?: boolean;
}

/**
 * Mappa il valore raw lato DB nella label visibile lato UI.
 * Convenzione: "-" rappresenta Produzione; "Bozzetto" e "Campione" restano come sono.
 */
function tipologiaLabel(raw: string): string {
  if (raw === '-' || raw.trim() === '-') return 'Produzione';
  return raw;
}

export function StoricoFilters({
  filters,
  onFiltersChange,
  onSearch,
  onReset,
  categorie,
  statiProgetto,
  tipologie = [],
  anni = [],
  loading = false,
}: StoricoFiltersProps) {
  const [opened, { toggle }] = useDisclosure(false);

  const handleFilterChange = (key: keyof StoricoFilter, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  const hasActiveFilters =
    filters.categoria ||
    filters.statoProgetto ||
    filters.searchTerm ||
    filters.startDate ||
    filters.endDate ||
    filters.year ||
    filters.projectNumber ||
    filters.tipologiaStatistica;

  // Costruisce le opzioni mantenendo il valore raw e mostrando la label leggibile.
  // Se nei dati esiste "-" senza altri valori normalizzati, lo elenchiamo come "Produzione".
  const tipologieOptions = tipologie
    .map((t) => ({ value: t, label: tipologiaLabel(t) }))
    // Ordina per label (Produzione vicino a Bozzetto/Campione anziché in fondo)
    .sort((a, b) => a.label.localeCompare(b.label));

  const anniOptions = anni.map((y) => ({ value: String(y), label: String(y) }));

  return (
    <Paper shadow="sm" p="md" withBorder>
      <Stack gap="md">
        {/* Barra di ricerca principale sempre visibile */}
        <Group justify="space-between" align="flex-end">
          <TextInput
            placeholder="Cerca per nome decoro, numero decoro, codice..."
            leftSection={<IconSearch size={16} />}
            value={filters.searchTerm || ''}
            onChange={(e) => handleFilterChange('searchTerm', e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSearch();
              }
            }}
            style={{ flex: 1 }}
            rightSection={
              filters.searchTerm ? (
                <ActionIcon
                  variant="subtle"
                  onClick={() => handleFilterChange('searchTerm', '')}
                >
                  <IconX size={16} />
                </ActionIcon>
              ) : null
            }
          />

          <Group gap="xs">
            <Button
              onClick={onSearch}
              loading={loading}
              leftSection={<IconSearch size={16} />}
            >
              Cerca
            </Button>

            <Button
              variant="light"
              onClick={toggle}
              rightSection={opened ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
              leftSection={<IconFilter size={16} />}
            >
              Filtri avanzati
              {hasActiveFilters && (
                <Text component="span" size="xs" ml={4} c="blue">
                  •
                </Text>
              )}
            </Button>

            {hasActiveFilters && (
              <Button variant="subtle" color="gray" onClick={onReset}>
                Reset
              </Button>
            )}
          </Group>
        </Group>

        {/* Filtri avanzati collassabili */}
        <Collapse in={opened}>
          <Stack gap="md" pt="md">
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Select
                  label="Tipologia"
                  placeholder="Tutte le tipologie"
                  data={tipologieOptions}
                  value={filters.tipologiaStatistica || null}
                  onChange={(value) =>
                    handleFilterChange('tipologiaStatistica', value || undefined)
                  }
                  clearable
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Select
                  label="Anno"
                  placeholder="Tutti gli anni"
                  data={anniOptions}
                  value={filters.year ? String(filters.year) : null}
                  onChange={(value) =>
                    handleFilterChange('year', value ? Number(value) : undefined)
                  }
                  clearable
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <TextInput
                  label="Numero progetto"
                  placeholder="es. riferimento o numDecoro"
                  value={filters.projectNumber || ''}
                  onChange={(e) =>
                    handleFilterChange('projectNumber', e.currentTarget.value || undefined)
                  }
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Select
                  label="Categoria"
                  placeholder="Tutte le categorie"
                  data={categorie.map((c) => ({ value: c, label: c }))}
                  value={filters.categoria || null}
                  onChange={(value) => handleFilterChange('categoria', value || undefined)}
                  clearable
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Select
                  label="Stato Progetto"
                  placeholder="Tutti gli stati"
                  data={statiProgetto.map((s) => ({ value: s, label: s }))}
                  value={filters.statoProgetto || null}
                  onChange={(value) => handleFilterChange('statoProgetto', value || undefined)}
                  clearable
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <DatePickerInput
                  label="Data inizio"
                  placeholder="Seleziona data"
                  value={filters.startDate ? new Date(filters.startDate) : null}
                  onChange={(date) =>
                    handleFilterChange('startDate', date ? date.toISOString() : undefined)
                  }
                  clearable
                  valueFormat="DD/MM/YYYY"
                />
              </Grid.Col>

              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <DatePickerInput
                  label="Data fine"
                  placeholder="Seleziona data"
                  value={filters.endDate ? new Date(filters.endDate) : null}
                  onChange={(date) =>
                    handleFilterChange('endDate', date ? date.toISOString() : undefined)
                  }
                  clearable
                  valueFormat="DD/MM/YYYY"
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}
